import { useEffect, useRef, useState } from "react";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { createLearningProgress } from "@/api/generated/learning-progresses/learning-progresses";
import { updateEnrollment } from "@/api/generated/enrollments/enrollments";

const DWELL_TIME_MS = 500;

/**
 * Marks a content element as completed once the user has seen the end of it.
 *
 * The returned `sentinelRef` must be attached to an (invisible) marker at the
 * bottom of the element; completion fires after that marker has been on
 * screen for `DWELL_TIME_MS`. "You reached the end of it" needs no geometry —
 * unlike visibility-percentage thresholds it works the same for a small card
 * and for a multi-section element spanning several screens.
 */
export const useContentElementProgress = (
  contentElementId: string,
  { autoComplete = true }: { autoComplete?: boolean } = {}
) => {
  const {
    enrollmentId,
    trainerMode,
    refreshProgress,
    completedContentElementIds,
    nodeIndex,
  } = useCourseDetailStore();

  const sentinelRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The store is the source of truth; `justCompleted` only bridges the gap
  // until refreshProgress has written our own completion back into it.
  const alreadyCompleted = completedContentElementIds.has(contentElementId);
  const [justCompleted, setJustCompleted] = useState(false);
  const isCompleted = alreadyCompleted || justCompleted;
  const sentRef = useRef(false); // dedup latch while a request is in flight

  useEffect(() => {
    if (
      !enrollmentId ||
      trainerMode ||
      !autoComplete ||
      !sentinelRef.current ||
      alreadyCompleted
    )
      return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        timerRef.current = setTimeout(async () => {
          if (sentRef.current) return;
          sentRef.current = true;
          try {
            await createLearningProgress(enrollmentId, {
              learningProgress: { contentElementId, status: "completed" },
            });

            const entry = nodeIndex.get(contentElementId);
            if (
              entry?.module &&
              entry.subchapter &&
              entry.moduleIndex !== null &&
              entry.chapterIndex !== null &&
              entry.contentElementIndex !== null &&
              entry.contentElementTotal !== null
            ) {
              updateEnrollment(enrollmentId, {
                enrollment: {
                  currentModuleTitle: entry.module.title,
                  currentPosition: {
                    moduleIndex: entry.moduleIndex,
                    chapterIndex: entry.chapterIndex,
                    subchapterId: entry.subchapter.id,
                    subchapterTitle: entry.subchapter.title,
                    contentElementId,
                    contentElementIndex: entry.contentElementIndex,
                    contentElementTotal: entry.contentElementTotal,
                  },
                },
              }).catch(() => {});
            }

            setJustCompleted(true);
            // Done for good — stop paying for scroll events on this element.
            observer.disconnect();
            await refreshProgress();
          } catch {
            sentRef.current = false;
          }
        }, DWELL_TIME_MS);
      } else if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    });

    observer.observe(sentinelRef.current);
    return () => {
      observer.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [
    enrollmentId,
    trainerMode,
    autoComplete,
    contentElementId,
    alreadyCompleted,
    nodeIndex,
    refreshProgress,
  ]);

  return { sentinelRef, isCompleted };
};
